import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Club, ClubEvent, Game } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import { apiService } from "../../services/api";
import {
  PhaseBackground,
  PhaseContainer,
  PhaseMain,
  PhaseButton,
  PlayersTable as BasePlayersTable,
  SectionCard,
  InstructionCard,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";
import PhaseHeader from "../Game/PhaseHeader";

interface ClubEventsProps {
  language: number;
  club: Club;
  initialEventId?: number;
  onStartGame: (
    clubId: number,
    eventId: number,
    tableId: number,
    nextGameNumber: number,
    participants: Participant[]
  ) => void;
  onBack: () => void;
}

interface Participant {
  id: number;
  user_id: number;
  nickname: string;
  count: number;
  killed_first: number;
  status: number; // 1 = pending, 2 = confirmed
  participation: number;
}

const EventButton = styled(PhaseButton)`
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  margin-bottom: ${theme.spacing.sm};
`;

const EventGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const SectionTitle = styled.h5`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.xl};
`;

const GameItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const GameLink = styled.a`
  flex: 1;
  text-decoration: none;
`;

const EventItem = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const EventItemButton = styled(PhaseButton)`
  flex: 1;
  justify-content: flex-start;
  text-align: left;
  margin: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const EventActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const EventActionButton = styled(PhaseButton)`
  padding: 0.75rem 1rem;
  min-width: 44px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1;
  }
`;

const CheckboxButton = styled.button.attrs({ type: "button" })<{
  $checked: boolean;
}>`
  font-size: 1.5rem;
  background: transparent;
  border: none;
  color: ${({ $checked }) =>
    $checked ? theme.colors.text.primary : theme.colors.text.muted};
  cursor: pointer;
  padding: 0;
  transition: all ${theme.transitions.normal};

  &:hover {
    transform: scale(1.1);
    color: ${theme.colors.text.primary};
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 0 -${theme.spacing.md};
    padding: 0 ${theme.spacing.md};
  }
`;

const PlayersTable = styled(BasePlayersTable)`
  min-width: 600px;

  thead th {
    white-space: nowrap;

    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: 0.75rem 0.75rem;
      font-size: 0.7rem;
    }
  }

  tbody td {
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: 0.75rem 0.75rem;
      font-size: 0.9rem;
    }
  }
`;

const InputGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const DateTimeGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
  
  > div {
    flex: 1;
  }
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
  font-size: 0.95rem;
  color: ${theme.colors.text.primary};
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  /* Date and Time pickers styling */
  &[type="date"],
  &[type="time"] {
    color: ${theme.colors.text.primary};
    font-size: 0.95rem;
    
    /* Better contrast for calendar/clock icons */
    &::-webkit-calendar-picker-indicator {
      filter: invert(1) brightness(1.8) contrast(1.2);
      cursor: pointer;
      opacity: 1;
      transition: opacity 0.2s ease, transform 0.2s ease;
      padding: 4px;
      
      &:hover {
        transform: scale(1.1);
      }
    }
    
    /* Firefox */
    &::-moz-calendar-picker-indicator {
      filter: invert(1) brightness(1.8) contrast(1.2);
      cursor: pointer;
    }
  }
  
  &[type="date"] {
    &::-webkit-inner-spin-button,
    &::-webkit-clear-button {
      display: none;
    }
  }
  
  &[type="time"] {
    &::-webkit-inner-spin-button {
      filter: invert(1) brightness(1.8) contrast(1.2);
      cursor: pointer;
    }
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-family: inherit;
  font-weight: 500;
  resize: vertical;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.75rem ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }
`;

const LinkBox = styled(InstructionCard)`
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  word-break: break-all;
  font-size: 0.9rem;
`;

const EmptyState = styled.p`
  font-style: italic;
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ConfirmedName = styled.span`
  text-decoration: underline;
  font-weight: 600;
`;

const ClubEvents: React.FC<ClubEventsProps> = ({
  language,
  club,
  initialEventId,
  onStartGame,
  onBack,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<ClubEvent | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDate, setNewEventDate] = useState(getTodayDate());
  const [newEventTime, setNewEventTime] = useState("19:00");
  const [newEventDescription, setNewEventDescription] = useState("");
  const [showDateWarning, setShowDateWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localClubEvents, setLocalClubEvents] = useState<ClubEvent[]>(
    club.clubEvents
  );
  const [showAllPastEvents, setShowAllPastEvents] = useState(false);

  const dict = dictionaries[language].ph;

  // Sync local events with club prop
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLocalClubEvents(club.clubEvents);
  }, [club.clubEvents]);

  // Auto-select event if initialEventId is provided
  useEffect(() => {
    if (initialEventId && localClubEvents.length > 0 && !selectedEvent) {
      const eventToSelect = localClubEvents.find(e => e.id === initialEventId);
      if (eventToSelect) {
        setSelectedEvent(eventToSelect);
      }
    }
  }, [initialEventId, localClubEvents]);

  useEffect(() => {
    if (selectedEvent) {
      window.scrollTo(0, 0);
      loadEventData(selectedEvent.id);
    }
  }, [selectedEvent]);

  function getTodayDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function getDateOnly(dateString: string): string {
    return dateString.split(' ')[0].split('T')[0];
  }

  function checkEventExistsOnDate(dateStr: string): boolean {
    return localClubEvents.some(event => 
      getDateOnly(event.start_date) === dateStr
    );
  }

  const loadEventData = async (eventId: number) => {
    setLoading(true);
    try {
      const response = await apiService.getClubGames(eventId);
      if (response.success) {
        setParticipants(
          response.success.registered.concat(response.success.unregistered)
        );
        setGames(response.success.games || []);
      }
    } catch (error) {
      console.error("Failed to load event data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (forceCreate = false) => {
    if (!newEventTitle.trim()) {
      alert(dict.enter_event_title);
      return;
    }

    // Check if event exists on this date
    if (!forceCreate && checkEventExistsOnDate(newEventDate)) {
      setShowDateWarning(true);
      return;
    }

    setShowDateWarning(false);
    setLoading(true);
    
    try {
      const fullDateTime = `${newEventDate} ${newEventTime}:00`;
      const response = await apiService.createClubEvent({
        club_id: club.id,
        title: newEventTitle,
        start_date: fullDateTime,
        description: newEventDescription,
        limit: 0,
        timeslots: [],
      });

      if (response.success) {
        // Add new event to local list
        const newEvent: ClubEvent = {
          id: response.success.eid,
          title: newEventTitle,
          start_date: fullDateTime,
          stage: 0,
          ranked: 1,
        };
        setLocalClubEvents(prev => [newEvent, ...prev]);
        
        // Reset form and close
        setShowCreateEvent(false);
        setNewEventTitle("");
        setNewEventDate(getTodayDate());
        setNewEventTime("19:00");
        setNewEventDescription("");
      }
    } catch (error) {
      console.error("Failed to create event:", error);
      alert(dict.failed_create_event);
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantStatusChange = async (
    participantId: number,
    newStatus: number
  ) => {
    try {
      await apiService.updateParticipantStatus(
        selectedEvent!.id,
        participantId,
        newStatus
      );
      // Refresh participants
      if (selectedEvent) {
        loadEventData(selectedEvent.id);
      }
    } catch (error) {
      console.error("Failed to update participant status:", error);
    }
  };

  const handleDeleteParticipant = async (participantId: number) => {
    if (!window.confirm(dict.confirm_remove_participant)) {
      return;
    }

    try {
      await apiService.deleteParticipant(selectedEvent!.id, participantId);
      // Refresh participants
      if (selectedEvent) {
        loadEventData(selectedEvent.id);
      }
    } catch (error) {
      console.error("Failed to delete participant:", error);
    }
  };

  const handleDeleteGame = async (gameId: number) => {
    if (!window.confirm(dict.confirm_delete_game)) {
      return;
    }

    try {
      await apiService.deleteGame(gameId);
      // Refresh games
      if (selectedEvent) {
        loadEventData(selectedEvent.id);
      }
    } catch (error) {
      console.error("Failed to delete game:", error);
    }
  };

  const handleEventAction = async (event: ClubEvent, action: "close" | "open" | "delete", e?: React.MouseEvent) => {
    // Prevent event bubbling to parent button
    if (e) {
      e.stopPropagation();
    }

    const confirmMessage =
      action === "delete"
        ? dict.confirm_delete_event
        : action === "close"
        ? dict.confirm_close_event
        : dict.confirm_open_event;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      switch (action) {
        case "close":
          await apiService.closeEvent(event.id);
          const closedEvent = { ...event, stage: 1 };
          if (selectedEvent?.id === event.id) {
            setSelectedEvent(closedEvent);
          }
          updateEventInList(closedEvent);
          break;
        case "open":
          await apiService.openEvent(event.id);
          const openedEvent = { ...event, stage: 0 };
          if (selectedEvent?.id === event.id) {
            setSelectedEvent(openedEvent);
          }
          updateEventInList(openedEvent);
          break;
        case "delete":
          await apiService.deleteEvent(event.id);
          setLocalClubEvents((prevEvents) =>
            prevEvents.filter((e) => e.id !== event.id)
          );
          if (selectedEvent?.id === event.id) {
            setSelectedEvent(null);
          }
          break;
      }

      if (action !== "delete" && selectedEvent?.id === event.id) {
        // Refresh event data if viewing this event
        loadEventData(event.id);
      }
    } catch (error) {
      console.error(`Failed to ${action} event:`, error);
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    // Month names by language
    const monthNames = [
      // English
      ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      // Russian
      ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],
      // Ukrainian
      ["січня", "лютого", "березня", "квітня", "травня", "червня", "липня", "серпня", "вересня", "жовтня", "листопада", "грудня"],
      // Georgian
      ["იანვარი", "თებერვალი", "მარტი", "აპრილი", "მაისი", "ივნისი", "ივლისი", "აგვისტო", "სექტემბერი", "ოქტომბერი", "ნოემბერი", "დეკემბერი"],
      // Hebrew
      ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"]
    ];

    const month = monthNames[language][monthIndex];

    return `${day} ${month} ${hours}:${minutes}`;
  };

  const updateEventInList = (updatedEvent: ClubEvent) => {
    setLocalClubEvents((prevEvents) =>
      prevEvents.map((event) =>
        event.id === updatedEvent.id ? updatedEvent : event
      )
    );
  };

  const getOpenEvents = () =>
    localClubEvents.filter((event) => event.stage === 0);
  const getPastEvents = () =>
    localClubEvents.filter((event) => event.stage !== 0);

  if (selectedEvent) {
    return (
      <PhaseBackground $variant="setup">
        <PhaseContainer>
          <PhaseHeader
            title={selectedEvent.title}
            subtitle={formatDateTime(selectedEvent.start_date)}
            variant="day"
            language={language}
            onTimeoutToggle={() => {}}
            hideTimer={true}
            actions={[
              {
                id: "back",
                label: dict.back,
                onClick: () => setSelectedEvent(null),
                variant: "secondary",
              },
            ]}
          />

          <PhaseMain>
            {/* Start Game Buttons - Only show for open events */}
            {selectedEvent.stage === 0 && (
              <SectionCard style={{ marginBottom: theme.spacing.xl }}>
                <SectionTitle>{dict.startclub}</SectionTitle>
                <ButtonGroup>
                  <PhaseButton
                    $variant="primary"
                    onClick={() => {
                      const table1Games = games.filter(
                        (g) => g.table_number === 1
                      );
                      const nextGameNumber =
                        table1Games.length > 0
                          ? Math.max(
                              ...table1Games.map((g) => g.session_number || 0)
                            ) + 1
                          : 1;
                      onStartGame(
                        club.id,
                        selectedEvent.id,
                        1,
                        nextGameNumber,
                        participants
                      );
                    }}
                  >
                    {dict.table} 1
                  </PhaseButton>
                  <PhaseButton
                    $variant="primary"
                    onClick={() => {
                      const table2Games = games.filter(
                        (g) => g.table_number === 2
                      );
                      const nextGameNumber =
                        table2Games.length > 0
                          ? Math.max(
                              ...table2Games.map((g) => g.session_number || 0)
                            ) + 1
                          : 1;
                      onStartGame(
                        club.id,
                        selectedEvent.id,
                        2,
                        nextGameNumber,
                        participants
                      );
                    }}
                  >
                    {dict.table} 2
                  </PhaseButton>
                </ButtonGroup>
              </SectionCard>
            )}

            {/* Participants - Only show for open events */}
            {selectedEvent.stage === 0 && (
              <SectionCard style={{ marginBottom: theme.spacing.xl }}>
              <SectionTitle>{dict.prtcp}</SectionTitle>
              {loading ? (
                <EmptyState>{dict.loading_participants}</EmptyState>
              ) : participants.length > 0 ? (
                <TableWrapper>
                  <PlayersTable>
                    <thead>
                      <tr>
                        <th>{dict.player}</th>
                        <th>{dict.games}</th>
                        <th>{dict.killed}</th>
                        <th>{dict.in}?</th>
                        <th>{dict.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participants.map((participant) => (
                        <tr key={participant.participation}>
                          <td>
                            {participant.status === 2 ? (
                              <ConfirmedName>
                                {participant.nickname}
                              </ConfirmedName>
                            ) : (
                              participant.nickname
                            )}
                          </td>
                          <td>{participant.count}</td>
                          <td>{participant.killed_first}</td>
                          <td>
                            <CheckboxButton
                              $checked={participant.status === 2}
                              onClick={() =>
                                handleParticipantStatusChange(
                                  participant.participation,
                                  participant.status === 2 ? 1 : 2
                                )
                              }
                            >
                              {participant.status === 2 ? "☑" : "☐"}
                            </CheckboxButton>
                          </td>
                          <td>
                            <PhaseButton
                              $variant="secondary"
                              onClick={() =>
                                handleDeleteParticipant(
                                  participant.participation
                                )
                              }
                              style={{
                                padding: "0.5rem 0.75rem",
                                fontSize: "0.9rem",
                              }}
                            >
                              🗑
                            </PhaseButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </PlayersTable>
                </TableWrapper>
              ) : (
                <EmptyState>{dict.none}</EmptyState>
              )}
              </SectionCard>
            )}

            {/* Games */}
            <SectionCard style={{ marginBottom: theme.spacing.xl }}>
              <SectionTitle>{dict.gamescaps}</SectionTitle>
              {games.length > 0 ? (
                <EventGrid>
                  {games.map((game) => (
                    <GameItem key={game.id}>
                      <GameLink
                        href={`https://mafiaworldtour.org/admin/V2/club_games/${game.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PhaseButton
                          $variant="outline"
                          style={{ width: "100%" }}
                        >
                          [{game.start_time}] {game.description}
                        </PhaseButton>
                      </GameLink>
                      <PhaseButton
                        $variant="secondary"
                        onClick={() => handleDeleteGame(game.id)}
                        style={{
                          padding: "0.5rem 0.75rem",
                          fontSize: "0.9rem",
                        }}
                      >
                        🗑
                      </PhaseButton>
                    </GameItem>
                  ))}
                </EventGrid>
              ) : (
                <EmptyState>{dict.none}</EmptyState>
              )}
            </SectionCard>

            {/* Event Page Link */}
            <SectionCard style={{ marginBottom: theme.spacing.xl }}>
              <SectionTitle>{dict.event_pa}</SectionTitle>
              <LinkBox>
                https://mafiaworldtour.org/events/{selectedEvent.id}
              </LinkBox>
              <PhaseButton
                $variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `https://mafiaworldtour.org/events/${selectedEvent.id}`
                  );
                }}
              >
                📋 {dict.copylink}
              </PhaseButton>
            </SectionCard>

            {/* Event Ranking */}
            <SectionCard>
              <SectionTitle>{dict.event_ranking}</SectionTitle>
              <StyledSelect
                value={selectedEvent.ranked}
                onChange={async (e) => {
                  const newRanked = parseInt(e.target.value);
                  try {
                    await apiService.updateEventRanking(
                      selectedEvent.id,
                      newRanked
                    );
                    // Update local state
                    const updatedEvent = {
                      ...selectedEvent,
                      ranked: newRanked,
                    };
                    setSelectedEvent(updatedEvent);
                    updateEventInList(updatedEvent);
                  } catch (error) {
                    console.error("Failed to update event ranking:", error);
                    alert(dict.failed_update_event_ranking);
                  }
                }}
              >
                <option value={1}>{dict.yrate}</option>
                <option value={0}>{dict.nrate}</option>
              </StyledSelect>
            </SectionCard>
          </PhaseMain>
        </PhaseContainer>
      </PhaseBackground>
    );
  }

  if (showCreateEvent) {
    return (
      <PhaseBackground $variant="setup">
        <PhaseContainer>
          <PhaseHeader
            title={dict.event_c}
            variant="day"
            language={language}
            onTimeoutToggle={() => {}}
            hideTimer={true}
            actions={[
              {
                id: "back",
                label: dict.back,
                onClick: () => setShowCreateEvent(false),
                variant: "secondary",
              },
            ]}
          />

          <PhaseMain>
            <SectionCard>
              <InputGroup>
                <InputLabel>{dict.event_title_label}</InputLabel>
                <StyledInput
                  type="text"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  placeholder={dict.event_t}
                />
              </InputGroup>

              <DateTimeGroup>
                <div>
                  <InputLabel>{dict.event_date_label}</InputLabel>
                  <StyledInput
                    type="date"
                    value={newEventDate}
                    onChange={(e) => {
                      setNewEventDate(e.target.value);
                      setShowDateWarning(false);
                    }}
                  />
                </div>
                <div>
                  <InputLabel>{dict.event_time_label || "Time"}</InputLabel>
                  <StyledInput
                    type="time"
                    value={newEventTime}
                    onChange={(e) => setNewEventTime(e.target.value)}
                  />
                </div>
              </DateTimeGroup>

              {showDateWarning && (
                <InstructionCard style={{ background: 'rgba(255, 77, 109, 0.18)', borderColor: 'rgba(255, 77, 109, 0.3)', marginBottom: theme.spacing.lg }}>
                  <p style={{ margin: 0, marginBottom: theme.spacing.md, color: '#ffb3c1' }}>
                    {dict.event_exists_warning || "An event already exists on this date. Do you want to create another one?"}
                  </p>
                  <ButtonGroup>
                    <PhaseButton
                      $variant="danger"
                      onClick={() => handleCreateEvent(true)}
                      disabled={loading}
                    >
                      {dict.yes || "Yes"}
                    </PhaseButton>
                    <PhaseButton
                      $variant="secondary"
                      onClick={() => setShowDateWarning(false)}
                    >
                      {dict.no || "No"}
                    </PhaseButton>
                  </ButtonGroup>
                </InstructionCard>
              )}

              <InputGroup>
                <InputLabel>{dict.event_description_label}</InputLabel>
                <StyledTextarea
                  rows={3}
                  value={newEventDescription}
                  onChange={(e) => setNewEventDescription(e.target.value)}
                  placeholder={dict.event_description_placeholder}
                />
              </InputGroup>

              {!showDateWarning && (
                <PhaseButton
                  $variant="primary"
                  onClick={() => handleCreateEvent(false)}
                  disabled={loading || !newEventTitle.trim()}
                >
                  {loading ? dict.creating : dict.create}
                </PhaseButton>
              )}
            </SectionCard>
          </PhaseMain>
        </PhaseContainer>
      </PhaseBackground>
    );
  }

  return (
    <PhaseBackground $variant="setup">
      <PhaseContainer>
        <PhaseHeader
          title={dict.club_prefix.replace("{0}", club.name)}
          variant="day"
          language={language}
          onTimeoutToggle={() => {}}
          hideTimer={true}
          actions={[
            {
              id: "back",
              label: dict.back,
              onClick: onBack,
              variant: "secondary",
            },
          ]}
        />

        <PhaseMain>
          {/* Open Events */}
          <SectionCard style={{ marginBottom: theme.spacing.xl }}>
            <SectionTitle>{dict.event_o}</SectionTitle>
            {getOpenEvents().length > 0 ? (
              <EventGrid>
                {getOpenEvents().map((event) => (
                  <EventItem key={event.id}>
                    <EventItemButton
                      $variant="primary"
                      onClick={() => setSelectedEvent(event)}
                    >
                      {formatDateTime(event.start_date)} | {event.title}
                    </EventItemButton>
                    <EventActionButtons>
                      <EventActionButton
                        $variant="secondary"
                        onClick={(e) => handleEventAction(event, "close", e)}
                        title={dict.close}
                      >
                        🔒
                      </EventActionButton>
                      <EventActionButton
                        $variant="secondary"
                        onClick={(e) => handleEventAction(event, "delete", e)}
                        title={dict.delete}
                      >
                        🗑
                      </EventActionButton>
                    </EventActionButtons>
                  </EventItem>
                ))}
              </EventGrid>
            ) : (
              <EmptyState>{dict.none}</EmptyState>
            )}
          </SectionCard>

          {/* Past Events */}
          <SectionCard style={{ marginBottom: theme.spacing.xl }}>
            <SectionTitle>{dict.event_p}</SectionTitle>
            {getPastEvents().length > 0 ? (
              <EventGrid>
                {getPastEvents()
                  .slice(0, showAllPastEvents ? undefined : 5)
                  .map((event) => (
                    <EventItem key={event.id}>
                      <EventItemButton
                        $variant="secondary"
                        onClick={() => setSelectedEvent(event)}
                      >
                        {formatDateTime(event.start_date)} | {event.title}
                      </EventItemButton>
                      <EventActionButtons>
                        <EventActionButton
                          $variant="secondary"
                          onClick={(e) => handleEventAction(event, "open", e)}
                          title={dict.reopen}
                        >
                          🔓
                        </EventActionButton>
                        <EventActionButton
                          $variant="secondary"
                          onClick={(e) => handleEventAction(event, "delete", e)}
                          title={dict.delete}
                        >
                          🗑
                        </EventActionButton>
                      </EventActionButtons>
                    </EventItem>
                  ))}
                {getPastEvents().length > 5 && (
                  <EventButton 
                    $variant="ghost"
                    onClick={() => setShowAllPastEvents(!showAllPastEvents)}
                  >
                    {showAllPastEvents 
                      ? dict.show_less || "Show Less"
                      : dict.more_events.replace(
                          "{0}",
                          String(getPastEvents().length - 5)
                        )
                    }
                  </EventButton>
                )}
              </EventGrid>
            ) : (
              <EmptyState>{dict.none}</EmptyState>
            )}
          </SectionCard>

          {/* Create Event */}
          <SectionCard>
            <SectionTitle>{dict.event_c}</SectionTitle>
            <PhaseButton
              $variant="primary"
              onClick={() => setShowCreateEvent(true)}
            >
              ➕ {dict.create}
            </PhaseButton>
          </SectionCard>
        </PhaseMain>
      </PhaseContainer>
    </PhaseBackground>
  );
};

export default ClubEvents;
